import { useEffect, useState } from "react";
import { useUser } from "@clerk/react";
import { CircleAlertIcon, LayersIcon, RefreshCwIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChipsInput } from "@/components/ui/chips-input";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { BatchSection } from "./BatchSection";
import { BatchesOverview } from "./BatchesOverview";
import { FleetPanel } from "./FleetPanel";
import { RelativeTime } from "./RelativeTime";
import { fmtClock } from "./format";
import type { BatchStatusResponse, CreatorQuery } from "./types";
import { REFRESH_INTERVAL_MS, useBatchStatus } from "./useBatchStatus";

const MAX_BATCHES = 10;
const BATCH_NAME_RE = /^[A-Za-z0-9_.-]{1,120}$/;
const LAST_BATCHES_KEY = "batch_status_batches";

function splitBatchNames(value: string | null | undefined): string[] {
  const names: string[] = [];
  for (const part of (value ?? "").split(/[\s,]+/)) {
    if (part && !names.includes(part)) names.push(part);
  }
  return names;
}

function loadLastBatches(): string[] {
  try {
    return splitBatchNames(localStorage.getItem(LAST_BATCHES_KEY));
  } catch {
    return [];
  }
}

function saveLastBatches(names: string[]) {
  try {
    localStorage.setItem(LAST_BATCHES_KEY, names.join(","));
  } catch {
    // Storage blocked — the list just won't survive a reload.
  }
}

/**
 * Staff-only Batch status (/manager/batch-status): sourcing and analysis
 * progress per raw_creators batch, with an all-batches overview and the
 * analysis fleet on top. Meant for the STAGING deployment, where the sourcing
 * pipeline runs. The batch list is kept in localStorage, so it survives
 * reloads and navigation; the server re-checks the Clerk uplodio_member flag
 * on every request.
 */
export default function BatchStatusPage() {
  const { user, isLoaded } = useUser();
  const isStaff = Boolean(
    (user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member
  );

  const [batchNames, setBatchNames] = useState<string[]>(loadLastBatches);
  useEffect(() => saveLastBatches(batchNames), [batchNames]);

  const [creatorQueries, setCreatorQueries] = useState<Record<string, CreatorQuery>>({});
  const { data, error, loading, refreshedAt, lastChange, fleetStuckSince, refresh } =
    useBatchStatus(batchNames, creatorQueries, isLoaded && isStaff);

  if (!isLoaded) return null;
  if (!isStaff) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        This page is for Uplodio staff only.
      </div>
    );
  }

  const addBatches = (value: string) => {
    const next = [...batchNames];
    for (const name of splitBatchNames(value)) {
      if (!next.includes(name)) next.push(name);
    }
    if (next.length > MAX_BATCHES) throw new Error(`At most ${MAX_BATCHES} batches at a time`);
    setBatchNames(next);
  };

  const removeBatch = (name: string) => {
    setBatchNames(batchNames.filter((n) => n !== name));
    setCreatorQueries((previous) => {
      const next = { ...previous };
      delete next[name];
      return next;
    });
  };

  return (
    <BatchStatusBoard
      batchNames={batchNames}
      data={data}
      error={error}
      loading={loading}
      refreshedAt={refreshedAt}
      lastChange={lastChange}
      fleetStuckSince={fleetStuckSince}
      onRefresh={() => void refresh()}
      onAddBatches={addBatches}
      onRemoveBatch={removeBatch}
      onCreatorQuery={(name, query) => setCreatorQueries((previous) => ({ ...previous, [name]: query }))}
    />
  );
}

export function BatchStatusBoard({
  batchNames,
  data,
  error,
  loading,
  refreshedAt,
  lastChange,
  fleetStuckSince,
  onRefresh,
  onAddBatches,
  onRemoveBatch,
  onCreatorQuery,
}: {
  batchNames: string[];
  data: BatchStatusResponse | null;
  error: string | null;
  loading: boolean;
  refreshedAt: number | null;
  lastChange: Record<string, number>;
  fleetStuckSince: number | null;
  onRefresh: () => void;
  onAddBatches: (value: string) => void;
  onRemoveBatch: (name: string) => void;
  onCreatorQuery: (batchName: string, query: CreatorQuery) => void;
}) {
  // Keep the page's order (the URL's), not the response's.
  const batches = batchNames
    .map((name) => data?.batches.find((b) => b.batch_name === name))
    .filter((b) => b !== undefined);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Batch status</h1>
          <p className="text-sm text-muted-foreground">
            Creator sourcing and analysis per raw_creators batch.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {refreshedAt ? (
              <>
                Refreshed {fmtClock(refreshedAt)} (<RelativeTime at={refreshedAt} />) · every{" "}
                {REFRESH_INTERVAL_MS / 1000}s
              </>
            ) : (
              "Loading…"
            )}
          </span>
          <Button variant="secondary" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCwIcon className={cn(loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>
            <CircleAlertIcon />
            Couldn't load the batch status
          </AlertTitle>
          <AlertDescription>
            {error}
            {data && " — showing the last successful refresh."}
          </AlertDescription>
        </Alert>
      )}

      <FleetPanel fleet={data?.fleet} stuckSince={fleetStuckSince} />

      <Frame>
        <FrameHeader>
          <FrameTitle>Batches</FrameTitle>
          <FrameDescription>
            raw_creators batch names (paste several, comma-separated). Creators
            re-found by a later batch stay counted in the batch that first
            inserted them.
          </FrameDescription>
        </FrameHeader>
        <FramePanel className="p-3">
          <ChipsInput
            items={batchNames}
            onAdd={onAddBatches}
            onRemove={onRemoveBatch}
            addLabel="Add batch"
            placeholder="freddies_insta_de_7"
            chipIcon={<LayersIcon className="size-3.5" />}
            transform={(value) => value.trim()}
            validate={(value) =>
              splitBatchNames(value).every((name) => BATCH_NAME_RE.test(name))
                ? null
                : "Batch names are letters, digits, _ . and - only"
            }
            maxItems={MAX_BATCHES}
          />
        </FramePanel>
        {batches.length > 1 && <BatchesOverview batches={batches} />}
      </Frame>

      {batchNames.length > 0 && !data && !error && (
        <Skeleton className="h-64 w-full rounded-xl" />
      )}

      {batches.map((batch) => (
        <BatchSection
          key={batch.batch_name}
          batch={batch}
          fleet={data?.fleet}
          lastChangeAt={lastChange[batch.batch_name]}
          onCreatorQuery={(query) => onCreatorQuery(batch.batch_name, query)}
          onRemove={() => onRemoveBatch(batch.batch_name)}
        />
      ))}
    </div>
  );
}
